import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import './TierLimitsManagement.css';
import {
  Layers, RefreshCw, Edit2, Save, X, Check, AlertCircle,
  MapPin, Package, Users, HardDrive, Grid3x3, Crown,
  TrendingUp, ChevronRight, Info, Video, FileText
} from 'lucide-react';

const TierLimitsManagement = () => {
  const [tiers, setTiers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit modal state
  const [editModal, setEditModal] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Merchants modal state
  const [merchantsModal, setMerchantsModal] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);

  // Limit type configurations
  const limitTypes = [
    { key: 'maxLocations', label: 'Locations', icon: MapPin, description: 'Store locations/branches' },
    { key: 'maxProducts', label: 'Products', icon: Package, description: 'Product listings' },
    { key: 'maxTeamMembers', label: 'Team Members', icon: Users, description: 'Staff accounts' },
    { key: 'maxStorage', label: 'Storage (MB)', icon: HardDrive, description: 'Media storage' },
    { key: 'maxVariationAttributes', label: 'Variant Attributes', icon: Grid3x3, description: 'Product variation types' },
    { key: 'maxVideoSizeMb', label: 'Video Size (MB)', icon: Video, description: 'Per-product video upload cap. Leave blank to disable videos for this tier (also requires the media.video feature).' },
    { key: 'maxDigitalAssetSizeMb', label: 'Digital File Size (MB)', icon: FileText, description: 'Per-product digital file (PDF/EPUB/ZIP) upload cap. Leave blank to disable file uploads for this tier (external URL delivery is always available). Also requires the media.digital feature.' }
  ];

  // Fetch tier comparison data
  const fetchTiers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/admin/tiers/comparison');
      if (response.success) {
        setTiers(response.tiers);
        setSummary(response.summary);
      }
    } catch (err) {
      console.error('Error fetching tiers:', err);
      setError('Failed to load tier data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTiers();
  }, [fetchTiers]);

  // Format limit value for display
  const formatLimit = (value, type) => {
    // Video / digital sizes are "Disabled" when null, not "Unlimited" —
    // uploads aren't available unless the admin sets a concrete cap (and
    // the merchant has the matching feature). External URLs for digital
    // products are NOT controlled by maxDigitalAssetSizeMb.
    if (type === 'maxVideoSizeMb' || type === 'maxDigitalAssetSizeMb') {
      if (value === null || value === undefined) return 'Disabled';
      if (value >= 1024) return `${(value / 1024).toFixed(1)} GB`;
      return `${value} MB`;
    }
    if (value === null || value === undefined) {
      return 'Unlimited';
    }
    if (type === 'maxStorage') {
      if (value >= 1000) {
        return `${(value / 1000).toFixed(1)} GB`;
      }
      return `${value} MB`;
    }
    return value.toLocaleString();
  };

  // Open edit modal
  const openEditModal = (tier, limitType) => {
    setEditModal({ tier, limitType });
    setEditValue(tier[limitType.key] === null ? '' : tier[limitType.key].toString());
    setSuccess('');
  };

  // Close edit modal
  const closeEditModal = () => {
    setEditModal(null);
    setEditValue('');
  };

  // Save limit change
  const handleSaveLimit = async () => {
    if (!editModal) return;

    try {
      setSaving(true);
      setError('');

      const value = editValue === '' ? null : parseInt(editValue);

      const response = await api.patch(`/admin/tiers/${editModal.tier.tier}/limits`, {
        limitType: editModal.limitType.key,
        value
      });

      if (response.success) {
        setSuccess(`Updated ${editModal.limitType.label} for ${editModal.tier.name} tier`);
        closeEditModal();
        fetchTiers(); // Refresh data
      }
    } catch (err) {
      console.error('Error updating limit:', err);
      setError('Failed to update limit');
    } finally {
      setSaving(false);
    }
  };

  // Open merchants modal
  const openMerchantsModal = async (tier) => {
    setMerchantsModal(tier);
    setMerchantsLoading(true);
    try {
      const response = await api.get(`/admin/tiers/${tier.tier}/merchants?limit=50`);
      if (response.success) {
        setMerchants(response.merchants);
      }
    } catch (err) {
      console.error('Error fetching merchants:', err);
    } finally {
      setMerchantsLoading(false);
    }
  };

  // Get tier color class
  const getTierColor = (tierName) => {
    const colors = {
      'FREE': 'tlm-tier-free',
      'PRO': 'tlm-tier-pro',
      'PREMIUM': 'tlm-tier-premium',
      'ENTERPRISE': 'tlm-tier-enterprise'
    };
    return colors[tierName] || 'tlm-tier-default';
  };

  // Auto-clear success message
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (loading) {
    return (
      <div className="tlm-container">
        <div className="tlm-loading">
          <RefreshCw className="tlm-spinner" size={32} />
          <p>Loading tier limits...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tlm-container">
      {/* Header */}
      <div className="tlm-header">
        <div className="tlm-header-left">
          <Layers className="tlm-header-icon" size={28} />
          <div>
            <h1>Tier Limits Management</h1>
            <p>Configure resource limits for each subscription tier</p>
          </div>
        </div>
        <button className="tlm-refresh-btn" onClick={fetchTiers} disabled={loading}>
          <RefreshCw size={18} className={loading ? 'tlm-spinner' : ''} />
          Refresh
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="tlm-alert tlm-alert-error">
          <AlertCircle size={18} />
          {error}
          <button onClick={() => setError('')}><X size={16} /></button>
        </div>
      )}
      {success && (
        <div className="tlm-alert tlm-alert-success">
          <Check size={18} />
          {success}
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="tlm-summary">
          <div className="tlm-summary-card">
            <Crown size={24} />
            <div>
              <span className="tlm-summary-value">{summary.tierCount}</span>
              <span className="tlm-summary-label">Active Tiers</span>
            </div>
          </div>
          <div className="tlm-summary-card">
            <Users size={24} />
            <div>
              <span className="tlm-summary-value">{summary.totalMerchants.toLocaleString()}</span>
              <span className="tlm-summary-label">Total Merchants</span>
            </div>
          </div>
          <div className="tlm-summary-card">
            <TrendingUp size={24} />
            <div>
              <span className="tlm-summary-value">{summary.totalOverrides}</span>
              <span className="tlm-summary-label">Custom Overrides</span>
            </div>
          </div>
        </div>
      )}

      {/* Tier Comparison Grid */}
      <div className="tlm-grid-container">
        <div className="tlm-grid">
          {/* Header Row */}
          <div className="tlm-grid-header">
            <div className="tlm-grid-cell tlm-label-cell">Limit Type</div>
            {tiers.map(tier => (
              <div key={tier.tier} className={`tlm-grid-cell tlm-tier-header ${getTierColor(tier.tier)}`}>
                <span className="tlm-tier-name">{tier.name}</span>
                <span className="tlm-tier-price">
                  {tier.monthlyPrice === 0 ? 'Free' : `₦${tier.monthlyPrice.toLocaleString()}/mo`}
                </span>
                <button
                  className="tlm-merchants-btn"
                  onClick={() => openMerchantsModal(tier)}
                >
                  {tier.merchantCount} merchants <ChevronRight size={14} />
                </button>
              </div>
            ))}
          </div>

          {/* Limit Rows */}
          {limitTypes.map(limitType => (
            <div key={limitType.key} className="tlm-grid-row">
              <div className="tlm-grid-cell tlm-label-cell">
                <limitType.icon size={18} />
                <div>
                  <span className="tlm-limit-name">{limitType.label}</span>
                  <span className="tlm-limit-desc">{limitType.description}</span>
                </div>
              </div>
              {tiers.map(tier => (
                <div key={`${tier.tier}-${limitType.key}`} className="tlm-grid-cell tlm-value-cell">
                  <span className={`tlm-limit-value ${tier[limitType.key] === null ? 'tlm-unlimited' : ''}`}>
                    {formatLimit(tier[limitType.key], limitType.key)}
                  </span>
                  <button
                    className="tlm-edit-btn"
                    onClick={() => openEditModal(tier, limitType)}
                    title={`Edit ${limitType.label} for ${tier.name}`}
                  >
                    <Edit2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Info Box */}
      <div className="tlm-info-box">
        <Info size={18} />
        <div>
          <strong>How tier limits work:</strong>
          <p>
            When you update a limit here, it applies to all merchants on that tier.
            Merchants with individual overrides (set in Location/User Management) will not be affected.
            When a merchant upgrades or downgrades their tier, they automatically get the new tier's limits.
          </p>
        </div>
      </div>

      {/* Edit Modal */}
      {editModal && (
        <div className="tlm-modal-overlay" onClick={closeEditModal}>
          <div className="tlm-modal" onClick={e => e.stopPropagation()}>
            <div className="tlm-modal-header">
              <h2>Edit {editModal.limitType.label}</h2>
              <button className="tlm-modal-close" onClick={closeEditModal}>
                <X size={20} />
              </button>
            </div>
            <div className="tlm-modal-body">
              <div className="tlm-modal-tier-badge" style={{ background: `var(--${getTierColor(editModal.tier.tier)})` }}>
                {editModal.tier.name} Tier
              </div>

              <div className="tlm-form-group">
                <label>
                  {editModal.limitType.label} Limit
                  <span className="tlm-form-hint">Leave empty for unlimited</span>
                </label>
                <input
                  type="number"
                  min="0"
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  placeholder="Unlimited"
                  className="tlm-input"
                />
              </div>

              <div className="tlm-impact-info">
                <AlertCircle size={16} />
                <span>
                  This change affects <strong>{editModal.tier.merchantCount - editModal.tier.merchantsWithOverrides}</strong> merchants
                  ({editModal.tier.merchantsWithOverrides} have custom overrides)
                </span>
              </div>
            </div>
            <div className="tlm-modal-footer">
              <button className="tlm-btn tlm-btn-secondary" onClick={closeEditModal}>
                Cancel
              </button>
              <button
                className="tlm-btn tlm-btn-primary"
                onClick={handleSaveLimit}
                disabled={saving}
              >
                {saving ? <RefreshCw size={16} className="tlm-spinner" /> : <Save size={16} />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merchants Modal */}
      {merchantsModal && (
        <div className="tlm-modal-overlay" onClick={() => setMerchantsModal(null)}>
          <div className="tlm-modal tlm-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="tlm-modal-header">
              <h2>{merchantsModal.name} Tier Merchants</h2>
              <button className="tlm-modal-close" onClick={() => setMerchantsModal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="tlm-modal-body">
              {merchantsLoading ? (
                <div className="tlm-loading-inline">
                  <RefreshCw className="tlm-spinner" size={20} />
                  Loading merchants...
                </div>
              ) : merchants.length === 0 ? (
                <div className="tlm-empty-state">
                  <Users size={48} />
                  <p>No merchants on this tier</p>
                </div>
              ) : (
                <div className="tlm-merchants-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Merchant</th>
                        <th>Store</th>
                        <th>Locations</th>
                        <th>Products</th>
                        <th>Overrides</th>
                      </tr>
                    </thead>
                    <tbody>
                      {merchants.map(m => (
                        <tr key={m.id}>
                          <td>
                            <div className="tlm-merchant-info">
                              <span className="tlm-merchant-email">{m.email}</span>
                              <span className="tlm-merchant-username">@{m.username}</span>
                            </div>
                          </td>
                          <td>{m.storeName || '-'}</td>
                          <td>{m.currentLocations}</td>
                          <td>{m.currentProducts}</td>
                          <td>
                            {m.hasOverrides ? (
                              <span className="tlm-badge tlm-badge-warning">Has Overrides</span>
                            ) : (
                              <span className="tlm-badge tlm-badge-default">Using Tier Defaults</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="tlm-modal-footer">
              <button className="tlm-btn tlm-btn-secondary" onClick={() => setMerchantsModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TierLimitsManagement;
